<template>
  <section id="home-cat" class="flex-full">
    <home-head-image />
    <breadcrumbs
      v-if="category && currentLocation"
      class="container pt20 pb20"
      :current-category="category"
      :current-tag="activeTag"
    />
    <home-store/>
    <category-seo-content
      v-if="category && currentLocation"
      :category="category"
      :tag="activeTag"
      :location="currentLocation"
      :stats="seoStats"
      :stores="stores"
      :sibling-categories="siblingCategories"
      :sibling-tags="siblingTags"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { s3Path, headBackgroundPath, isServer } from '@vue-storefront/core/helpers';
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue';
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus';
const HomeHeadImage = () => import(/* webpackChunkName: "vsf-component-home-head-image" */ 'theme/components/core/blocks/MainSlider/HomeHeadImage');
const HomeStore = () => import(/* webpackChunkName: "vsf-component-home-store" */ 'theme/components/core/blocks/Switcher/HomeStore');
const CategorySeoContent = () => import(/* webpackChunkName: "vsf-component-category-seo-content" */ 'theme/components/theme/blocks/Partners/CategorySeoContent');

export default {
  components: {
    HomeHeadImage,
    HomeStore,
    CategorySeoContent,
    Breadcrumbs
  },
  // Prefetches the category/tag+location SEO stats server-side (K2-2985) so
  // the templated H1/meta/intro/FAQ/schema are present in the SSR response,
  // not only after client-side hydration.
  async asyncData ({ store, route }) {
    if (!route.params.listId) {
      return
    }
    if (!store.getters['locations/getLocations'].length) {
      await store.dispatch('locations/loadLocations')
    }
    const locationCode = route.params.locationCode
    const activeLocation = store.getters['locations/getActiveLocation']
    // Switching city reloads the cart and the city's list, so only do it on a real change.
    if (locationCode && (!activeLocation || activeLocation.code !== locationCode)) {
      await store.dispatch('locations/setCurrentLocation', { code: locationCode })
    } else {
      await store.dispatch('partners/resetCurrentPartner')
    }
    await store.dispatch('partner_categories/ensureCategories', { locationCode })
    const category = store.getters['partner_categories/getCategoryByCode'](route.params.listId)
    if (!category || !category.entity_id) {
      return
    }
    await store.dispatch('partner_tags/ensureTags', { categoryId: category.entity_id, locationCode })
    const tag = route.params.tags
      ? store.getters['partner_tags/getTags'].find(t => t.code === route.params.tags)
      : null
    // A tag-less URL must clear a previously selected tag; HomeStore refetches
    // its list on 'reset-load-partners'.
    if (!tag && store.getters['partner_tags/getSelectedTags'].length) {
      await store.dispatch('partner_tags/resetSelectedTags')
      if (!isServer) {
        EventBus.$emit('reset-load-partners')
      }
    }
    if (isServer) {
      // Page 1 of the grid; on the client HomeStore's infinite loading fetches it.
      await store.dispatch('partners/loadPartners', { locationCode, categoryId: category.entity_id, tag, size: 12, from: 0 })
    }
    // Stats are a nice-to-have enhancement (numbers in title/intro/FAQ) -
    // if the endpoint is down, the page must still render with the
    // non-numeric parts of the template rather than failing SSR entirely.
    try {
      if (tag) {
        await store.dispatch('partners/categorySeoStats', { tagId: tag.entity_id, locationCode })
      } else {
        await store.dispatch('partners/categorySeoStats', { categoryId: category.entity_id, locationCode })
      }
    } catch (e) {
      console.error(e)
    }
  },
  computed: {
    ...mapGetters({
      device: 'ui/getDevice',
      currentLocation: 'locations/getActiveLocation',
      getCategoryByCode: 'partner_categories/getCategoryByCode',
      getCategories: 'partner_categories/getCategories',
      getTags: 'partner_tags/getTags',
      seoStats: 'partners/getCategorySeoStats',
      getPartnersByCategory: 'partners/getPartnersByCategory'
    }),
    category () {
      return this.getCategoryByCode(this.$route.params.listId)
    },
    activeTag () {
      return this.$route.params.tags
        ? this.getTags.find(t => t.code === this.$route.params.tags)
        : null
    },
    stores () {
      return this.$route.params.listId ? this.getPartnersByCategory(this.$route.params.listId) : []
    },
    // Full candidate list, unsliced - CategorySeoContent.vue ranks by relevance
    // (partner overlap with the current page) before taking its own top 5, so
    // slicing here first would just make that ranking pick from an already-
    // arbitrary subset.
    siblingCategories () {
      if (!this.category) {
        return []
      }
      return this.getCategories.filter(cat => cat.code !== this.category.code)
    },
    siblingTags () {
      if (!this.activeTag) {
        return []
      }
      return this.getTags.filter(tag => tag.code !== this.activeTag.code)
    }
  },
  metaInfo () {
    return {
      link: [
        { rel: 'preload', as: 'image', href: s3Path(headBackgroundPath(this.device)) }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
    #home-cat{
        position: relative;
    }
    .new-collection {
        @media (max-width: 768px) {
            padding-top: 0;
        }
    }
    .menu-bottom {
        display: block;
        margin-top: -50px;
    }
</style>
